import { FieldDependencyCondition, FieldDependencyRule, FieldDependencyWhen } from '../model/field-dependency';
import { getValueByPath, normalizeBooleanComparison } from '../../utils/value-utils';

export function hasRequiredEffects(rules: FieldDependencyRule[]): boolean {
  return rules.some(rule => rule.then.required !== undefined || rule.otherwise?.required !== undefined);
}

export function getRequiredFromDependencies(
  rules: FieldDependencyRule[],
  values: Record<string, any>,
  baseRequired: boolean
): boolean {
  let requiredOverride: boolean | undefined;

  for (const rule of rules) {
    const matched = evaluateWhen(rule.when, values);
    const effects = matched ? rule.then : rule.otherwise;
    if (!effects) continue;

    if (effects.required !== undefined) {
      requiredOverride = effects.required;
    }
  }

  return requiredOverride ?? baseRequired;
}

export function evaluateWhen(when: FieldDependencyWhen, values: Record<string, any>): boolean {
  if (isCondition(when)) {
    return evaluateCondition(when, values);
  }

  const all = when.all ?? [];
  const any = when.any ?? [];

  const allMatch = all.length === 0 || all.every(condition => evaluateCondition(condition, values));
  const anyMatch = any.length === 0 || any.some(condition => evaluateCondition(condition, values));

  return allMatch && anyMatch;
}

function isCondition(when: FieldDependencyWhen): when is FieldDependencyCondition {
  return (when as FieldDependencyCondition).field !== undefined;
}

function evaluateCondition(condition: FieldDependencyCondition, values: Record<string, any>): boolean {
  const operator = condition.operator ?? 'eq';
  let left = getValueByPath(values, condition.field);
  let right = condition.value;

  if (operator === 'eq' || operator === 'neq') {
    const normalized = normalizeBooleanComparison(left, right);
    left = normalized.left;
    right = normalized.right;
  }

  switch (operator) {
    case 'eq':
      return left === right;
    case 'neq':
      return left !== right;
    case 'truthy':
      return !!left;
    case 'falsy':
      return !left;
    default:
      return false;
  }
}
